"use client";

import { useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";

const INTEGRATIONS = [
{
  name: "Google Drive",
  description: "Sync folders automatically. New documents ingest within minutes of being added.",
  icon: "https://img.rocket.new/generatedImages/rocket_gen_img_14166aa8f-1772388722522.png",
  alt: "Google Drive logo - colorful document sync icon",
  tag: "Auto-sync",
  tagColor: "#0FF4C6"
},
{
  name: "SharePoint",
  description: "Connect enterprise SharePoint libraries. Respects your existing permission structure.",
  icon: "https://img.rocket.new/generatedImages/rocket_gen_img_1980e988b-1772388726563.png",
  alt: "Microsoft SharePoint logo - blue enterprise document management icon",
  tag: "Enterprise",
  tagColor: "#818CF8"
},
{
  name: "Web URLs",
  description: "Watch any public URL — investor pages, press release feeds, clinical trial registries.",
  icon: "https://img.rocket.new/generatedImages/rocket_gen_img_154e7b15a-1772388726706.png",
  alt: "Web browser icon representing URL monitoring and web scraping capability",
  tag: "Scheduled refresh",
  tagColor: "#F5A623"
},
{
  name: "Direct Upload",
  description: "Drag and drop PDFs, DOCX, or PPTX files. Processed and searchable within 60 seconds.",
  icon: "https://img.rocket.new/generatedImages/rocket_gen_img_1e217d2f7-1772388722536.png",
  alt: "PDF file icon representing direct document upload capability",
  tag: "Instant",
  tagColor: "#0FF4C6"
}];


const SUPPORTED_FORMATS = [
"PDF", "DOCX", "PPTX", "XLSX", "HTML",
"TXT", "MD", "CSV", "EPUB", "RTF"];


export default function IntegrationsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".reveal, .reveal-left, .reveal-right").forEach((el, i) => {
              setTimeout(() => el.classList.add("visible"), i * 100);
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="section-pad relative overflow-hidden"
      id="integrations"
      style={{ background: "linear-gradient(180deg, #0D1526 0%, #0B0F1A 100%)" }}>
      
      <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
      <div
        className="absolute top-0 right-0 w-[500px] h-[400px] pointer-events-none"
        style={{ background: "radial-gradient(ellipse 70% 60% at 100% 0%, rgba(15,244,198,0.05), transparent)" }} />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="mb-16">
          <div className="reveal">
            <span className="section-label">Data Ingestion</span>
          </div>
          <div className="grid lg:grid-cols-2 gap-8 items-end">
            <div className="reveal">
              <h2
                className="font-display text-4xl lg:text-5xl font-medium text-white leading-tight"
                style={{ fontFamily: "Fraunces, serif" }}>
                
                Connect your documents,
                <br />
                <span className="gradient-text-teal">wherever they live</span>
              </h2>
            </div>
            <div className="reveal" style={{ transitionDelay: "100ms" }}>
              <p className="text-[#8892A4] text-base leading-relaxed">
                Myndlayer meets your documents where they already are. Connect multiple sources simultaneously — every new document added to a watched folder or URL is automatically ingested and made searchable within minutes.
              </p>
            </div>
          </div>
        </div>

        {/* Integration cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {INTEGRATIONS?.map((integration, i) =>
          <div
            key={integration?.name}
            className="integration-card reveal"
            style={{ transitionDelay: `${i * 80}ms` }}>
            
              {/* Icon */}
              <div className="w-12 h-12 rounded-xl overflow-hidden flex items-center justify-center"
            style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)" }}>
              
                <AppImage
                src={integration?.icon}
                alt={integration?.alt}
                width={32}
                height={32}
                className="w-8 h-8 object-contain" />
              
              </div>

              {/* Name + tag */}
              <div className="text-center">
                <h3 className="text-sm font-semibold text-white mb-1">{integration?.name}</h3>
                <span
                className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold"
                style={{
                  background: `${integration?.tagColor}12`,
                  color: integration?.tagColor,
                  border: `1px solid ${integration?.tagColor}25`
                }}>
                
                  {integration?.tag}
                </span>
              </div>

              <p className="text-[11px] text-[#4B5568] text-center leading-relaxed">
                {integration?.description}
              </p>
            </div>
          )}
        </div>

        {/* Supported formats */}
        <div
          className="rounded-2xl p-6 lg:p-8 reveal"
          style={{ background: "#111827", border: "1px solid rgba(255,255,255,0.06)" }}>
          
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="flex-shrink-0">
              <p className="text-xs font-semibold text-white mb-1">Supported file formats</p>
              <p className="text-[11px] text-[#4B5568]">All major document types supported</p>
            </div>
            <div className="flex flex-wrap gap-2 flex-1">
              {SUPPORTED_FORMATS?.map((fmt) =>
              <span
                key={fmt}
                className="px-2.5 py-1 rounded-lg text-[11px] font-mono font-medium text-[#8892A4]"
                style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)" }}>
                
                  .{fmt?.toLowerCase()}
                </span>
              )}
            </div>
            <div className="flex-shrink-0">
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-[#0FF4C6] animate-teal-pulse" />
                <span className="text-xs text-[#0FF4C6] font-medium">60s avg. ingest time</span>
              </div>
            </div>
          </div>
        </div>

        {/* Ingestion flow visualization */}
        <div className="mt-10 reveal" style={{ transitionDelay: "100ms" }}>
          <div
            className="rounded-2xl p-6 lg:p-8 overflow-hidden relative"
            style={{
              background: "linear-gradient(135deg, rgba(15,244,198,0.04), rgba(11,15,26,0.8))",
              border: "1px solid rgba(15,244,198,0.1)"
            }}>
            
            <div
              className="absolute top-0 left-0 right-0 h-px"
              style={{ background: "linear-gradient(90deg, transparent, rgba(15,244,198,0.3), transparent)" }} />
            
            <div className="flex flex-col md:flex-row items-center gap-4 md:gap-0 justify-between">
              {[
              { label: "Source Document", icon: "📄", sub: "PDF / DOCX / URL" },
              { label: "→", icon: null, sub: null },
              { label: "Chunking & Embedding", icon: "⚙️", sub: "text-embedding-004" },
              { label: "→", icon: null, sub: null },
              { label: "Vector Store", icon: "🗄️", sub: "Tenant-isolated namespace" },
              { label: "→", icon: null, sub: null },
              { label: "RAG Retrieval", icon: "🔍", sub: "Top-k semantic search" },
              { label: "→", icon: null, sub: null },
              { label: "AI Answer", icon: "✨", sub: "With inline citations" }]?.
              map((step, i) =>
              step?.icon ?
              <div key={i} className="flex flex-col items-center gap-1 text-center">
                    <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-lg"
                  style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)" }}>
                  
                      {step?.icon}
                    </div>
                    <span className="text-[10px] font-semibold text-white whitespace-nowrap">{step?.label}</span>
                    <span className="text-[9px] text-[#4B5568] whitespace-nowrap">{step?.sub}</span>
                  </div> :

              <div key={i} className="text-[#2D3748] text-lg hidden md:block">→</div>

              )}
            </div>
          </div>
        </div>
      </div>
    </section>);

}